import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/shared/PageHeader";
import { DataTable } from "@/components/shared/DataTable";
import { EmptyState } from "@/components/shared/EmptyState";
import { Users, Bot } from "lucide-react";
import { toast } from "sonner";

export default function PeopleTeam() {
  const [teammates, setTeammates] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = useCallback(() => {
    return api.get("/directory").then((r) => {
      setTeammates(r.data.teammates || []);
      setCanManage(!!r.data.can_manage);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const managers = teammates.filter((t) => t.kind === "person");

  const changeManager = async (row, value) => {
    setSaving(row.id);
    try {
      await api.put(`/admin/users/${row.id}/reports-to`, { reports_to: value || null });
      toast.success(value ? "Reporting line updated" : "Reporting line cleared");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update reporting line");
    } finally {
      setSaving(null);
    }
  };

  const reportsToCell = (r) => {
    if (canManage && r.kind === "person") {
      return (
        <select
          data-testid={`reports-to-select-${r.id}`}
          className="border border-slate-200 rounded-md text-sm py-1 px-2 bg-white max-w-[200px] disabled:opacity-50"
          value={r.reports_to || ""}
          disabled={saving === r.id}
          onChange={(e) => changeManager(r, e.target.value)}
        >
          <option value="">No manager</option>
          {managers.filter((m) => m.id !== r.id).map((m) => (
            <option key={m.id} value={m.id}>{m.name || m.email}</option>
          ))}
        </select>
      );
    }
    return r.reports_to_name
      ? <span data-testid={`reports-to-${r.id}`}>{r.reports_to_name}</span>
      : <span className="text-slate-300">No manager</span>;
  };

  const columns = [
    { key: "name", header: "Name", render: (r) => (
      <span className="flex items-center gap-2 font-medium text-slate-900">
        {r.kind === "agent" ? <Bot className="w-4 h-4 text-indigo-500" /> : <Users className="w-4 h-4 text-slate-400" />}
        {r.name || <span className="text-slate-300">Unnamed</span>}
        {r.is_provider && <span className="chip border-orange-200 text-orange-700 bg-orange-50" data-testid={`provider-badge-${r.id}`}>Total Operations</span>}
      </span>
    ) },
    { key: "kind", header: "Type", render: (r) => (
      <span className={`chip ${r.kind === "agent" ? "border-indigo-200 text-indigo-700 bg-indigo-50" : "border-slate-200 text-slate-600 bg-slate-50"}`} data-testid={`teammate-kind-${r.id}`}>
        {r.kind === "agent" ? "AI agent" : "Person"}
      </span>
    ) },
    { key: "reports_to", header: "Reports to", render: reportsToCell },
    { key: "roles", header: "Roles", render: (r) => (r.roles || []).join(", ") || <span className="text-slate-300">None</span> },
    { key: "email", header: "Email", render: (r) => r.email || <span className="text-slate-300">None</span> },
    { key: "status", header: "Status", render: (r) => (
      <span className={`chip ${r.status === "active" ? "border-emerald-200 text-emerald-700 bg-emerald-50" : "border-amber-200 text-amber-700 bg-amber-50"}`}>{r.status}</span>
    ) },
  ];

  return (
    <div data-testid="people-team-page">
      <PageHeader title="Team" subtitle="Everyone in this company: teammates and AI agents, one shared roster" />
      {!loading && teammates.length === 0 ? (
        <EmptyState icon={Users} title="No teammates yet" description="Invite people or hire agents and they will appear here in one shared directory." />
      ) : (
        <DataTable testid="people-team-table" rowTestId="teammate-row" columns={columns} rows={teammates} />
      )}
    </div>
  );
}
